import MarkdownText from './MarkdownText'
import { parseSections, visibleSections } from '../utils/descriptionSections'

/**
 * Renders an entity's progressive reveals.
 *
 * Read-only (default): revealed blocks only, as flowing prose — the entry
 * simply reads longer than it used to, with no headings and no sign that a
 * hidden block exists.
 *
 * Editable (admin, in the detail modal): every block, each with a
 * Hidden/Revealed toggle that flips it live mid-session.
 *
 * Read-only mode filters on `revealed` itself even though the server already
 * stripped hidden blocks for players: "view as player" is a frontend-only
 * toggle, so the server still sends hidden blocks to a previewing admin.
 */
export default function DescriptionSections({ sections, editable = false, onSetRevealed }) {
  const all = parseSections(sections)
  // In editable mode this holds the unfiltered `all` array and is used only
  // as an emptiness gate below; in read-only mode it's the actual list
  // rendered further down.
  const rendered = editable ? all : visibleSections(all)
  if (rendered.length === 0) return null

  if (!editable) {
    // Plain block flow, not a flex column: a flex container won't wrap
    // around the detail modals' floated portrait (see .detail-float), so
    // reveals that start beside it would stay narrow all the way down.
    return (
      <div style={{ marginTop: '1rem' }}>
        {rendered.map((section, i) => (
          <div key={i} style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '1rem', marginTop: i ? '1rem' : 0 }}>
            <MarkdownText>{section.body}</MarkdownText>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '1rem' }}>
      {all.map((section, i) => (
        <div
          key={i}
          style={{
            border: '1px solid var(--border-color)',
            borderLeft: `3px solid ${section.revealed ? '#2ecc71' : 'var(--text-muted)'}`,
            borderRadius: '8px',
            background: section.revealed ? 'rgba(46, 204, 113, 0.05)' : 'var(--bg-secondary)',
            padding: '0.5rem 0.75rem 0.7rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', fontWeight: 700 }}>{i + 1}</span>
            <span style={{ flexGrow: 1 }} />
            <button
              type="button"
              onClick={() => onSetRevealed?.(i, !section.revealed)}
              title={section.revealed ? 'Hide this from players again' : 'Reveal this to players now'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.2rem 0.6rem',
                borderRadius: '999px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 600,
                background: section.revealed ? 'rgba(46, 204, 113, 0.13)' : 'var(--hover-bg)',
                border: `1px solid ${section.revealed ? '#27ae60' : 'var(--border-color)'}`,
                color: section.revealed ? '#2ecc71' : 'var(--text-muted)',
              }}
            >
              {section.revealed ? 'Revealed' : 'Hidden'}
            </button>
          </div>
          <MarkdownText
            style={{
              whiteSpace: 'pre-line',
              color: section.revealed ? 'var(--text-secondary)' : 'var(--text-muted)',
            }}
          >
            {section.body}
          </MarkdownText>
        </div>
      ))}
    </div>
  )
}
